import { KEEP_PLACE } from "@/lib/search";
import { errorWords } from "@alm/domain";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { useFacility } from "@/lib/facility/facility";
import { ArrowLeft, Phone } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCan, useCanAnywhere } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { cn } from "@/lib/utils";
import { useZoneFor } from "@/lib/facility/zone";
import { dayLabel, otherBuilding } from "./-components/call-facts";
import { callDayKey } from "@/routes/_authenticated/_front-desk/sarah/-components/call-format";
import { attribute } from "./-components/attribution";
import { mergePages, nextOffset, PAGE } from "./-components/call-pages";
import { useHandledCheck } from "@/routes/_authenticated/_front-desk/sarah/-components/use-handled-check";
import { CallLine } from "./-components/call-line";
import { CallPane } from "./-components/call-pane";
import {
  CallTabs,
  emptyTitle,
  isLine,
  isTab,
  listSearch,
  type CallSearch,
} from "./-components/call-tabs";
import { LineSwitch } from "./-components/line-switch";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { SearchField } from "@/components/app/patterns/search-field";

export const Route = createFileRoute("/_authenticated/ws/sarah/calls")({
  validateSearch: (search: Record<string, unknown>): CallSearch => ({
    ...(typeof search.c === "string" && search.c ? { c: search.c } : {}),
    ...(isTab(search.tab) && search.tab !== "all" ? { tab: search.tab } : {}),
    ...(isLine(search.line) ? { line: search.line } : {}),
    ...(typeof search.q === "string" && search.q.trim()
      ? { q: search.q.trim().slice(0, 120) }
      : {}),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "sarah-calls"),
  component: SarahCalls,
});

/** Four views: the ones the desk switches between. Transfers, robocalls and the person, date and
 * resident filters stay in Core's call log. Someone with a company number of their own has a second
 * history, as Texts does, never mixed with the front desk's line. */
function SarahCalls() {
  const { c, tab: asked = "all", line, q: needle } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { facilityId } = useFacility();
  const zoneFor = useZoneFor();
  const canClose = useCan("frontdesk:call");
  const canDial = useCanAnywhere("frontdesk:call");
  const queryClient = useQueryClient();
  const { text: q, change: setQ } = useSearchParamText(
    needle,
    (next) =>
      void navigate({
        search: (prev) => ({ ...prev, q: next }),
        ...KEEP_PLACE,
      }),
  );

  /* The same read as Texts and Make a call, so the three pages share one answer. */
  const myLine = useQuery({
    ...orpc.frontDesk.phone.myLine.queryOptions({ input: { limit: 50 } }),
    enabled: canDial,
    staleTime: 15_000,
  });
  const own = myLine.data?.lines[0];
  /* Every call on your own number is yours, so there Mine reads as All. */
  const tab = line && asked === "mine" ? "all" : asked;

  const list = useInfiniteQuery({
    ...orpc.frontDesk.calls.list.infiniteOptions({
      input: (offset: number) => ({
        facilityId: facilityId === "all" ? undefined : facilityId,
        tab: tab === "mine" ? "all" : tab,
        /* With a number of your own, the front desk's history is the desk's alone. */
        line: line ? "own" : own ? "desk" : undefined,
        search: needle,
        limit: PAGE,
        offset,
      }),
      initialPageParam: 0,
      getNextPageParam: nextOffset,
    }),
    /* Which line to ask for is known once the reader's numbers are: asking before that lists
       the two together, then takes half away once the answer comes. */
    enabled: !myLine.isLoading,
    /* The desk leaves this open all day, so the newest page keeps itself fresh. Once somebody has
     * paged back it stops: refetching an infinite query refetches every page it holds, and each one
     * costs the tab counts and the facets again. The older pages are history and do not move. */
    refetchInterval: (query) => ((query.state.data?.pages.length ?? 1) > 1 ? false : 15_000),
  });
  const triage = useMutation(
    orpc.frontDesk.calls.triage.mutationOptions({
      onSuccess: async () => {
        toast.success("Marked handled");
        await queryClient.invalidateQueries({ queryKey: orpc.frontDesk.calls.key() });
      },
      onError: (error: Error, input) => {
        if (!check.recover(error, input.callId)) toast.error(errorWords(error));
      },
    }),
  );
  const check = useHandledCheck(triage.mutate);

  const me = useQuery(orpc.you.deskLine.queryOptions());
  const roster = useQuery({
    ...orpc.frontDesk.staff.list.queryOptions({
      input: { facilityId: facilityId === "all" ? undefined : facilityId },
    }),
    staleTime: 60_000,
  });

  const pages = list.data?.pages ?? [];
  const fetched = mergePages(pages);
  const rows =
    tab === "mine" ? fetched.filter((row) => attribute(row, me.data, roster.data).mine) : fetched;
  const counts = pages[0]?.counts;
  const total = pages[0]?.total ?? 0;

  /* Grouped by the building's own day, so "Today" and "Yesterday" mean what
     they do to the people who took the calls. */
  const days: { key: string; rows: typeof rows }[] = [];
  for (const row of rows) {
    const key = callDayKey(new Date(row.startedAt), zoneFor(row.facilityId));
    const last = days.at(-1);
    if (last?.key === key) last.rows.push(row);
    else days.push({ key, rows: [row] });
  }
  const todayKey = callDayKey(new Date(), zoneFor(null));
  /* Beside the list there is room for a call, so the newest is open rather than a blank half. */
  const mobile = useIsMobile();
  const shown = c ?? (mobile ? undefined : rows[0]?.id);

  return (
    /* Held to the window beside the list from `md`; on a phone the list scrolls with the page,
       so the bars can tuck away and give it the screen. */
    <div className="-m-4 flex md:-m-6 md:h-[calc(100svh-3.5rem-var(--dock-h,0px))] lg:-m-8">
      <h1 className="sr-only">Calls</h1>
      {check.dialog}
      <aside
        className={cn(
          "flex w-full shrink-0 flex-col border-r md:w-[26rem]",
          c ? "hidden md:flex" : "flex",
        )}
      >
        <div className="space-y-3 border-b p-3">
          {own ? (
            <LineSwitch
              value={line ? "mine" : "desk"}
              number={own.phoneNumber}
              className="sm:w-full"
              onChange={(next) =>
                void navigate({
                  search: listSearch(tab, next === "mine" ? "mine" : undefined, needle),
                })
              }
            />
          ) : null}
          <CallTabs tab={tab} line={line} q={needle} counts={counts} />
          <SearchField
            className="sm:w-full"
            value={q}
            onChange={setQ}
            placeholder="Search what was said"
            label="Search calls"
          />
        </div>
        <div className="min-h-0 flex-1 p-2 md:overflow-y-auto">
          {list.isPending ? (
            <div className="space-y-2 p-1" aria-busy="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-12 w-full rounded-xl" />
              ))}
            </div>
          ) : list.isError ? (
            <ReadFailed
              message="Couldn't load the calls."
              error={list.error}
              onRetry={() => void list.refetch()}
              retrying={list.isRefetching}
              className="p-3"
            />
          ) : (
            <>
              {rows.length > 0 ? (
                days.map((day) => (
                  <section key={day.key} className="pb-2">
                    <h2 className="text-faint px-3 pt-2 pb-1 text-xs font-medium">
                      {dayLabel(day.key, todayKey)}
                    </h2>
                    <ul className="space-y-0.5">
                      {day.rows.map((row) => (
                        <CallLine
                          key={row.id}
                          row={row}
                          timeZone={zoneFor(row.facilityId)}
                          me={me.data}
                          roster={roster.data}
                          selected={row.id === shown}
                          underDay
                          search={listSearch(tab, line, needle)}
                          building={otherBuilding(row, facilityId)}
                          onHandled={canClose ? check.tick : undefined}
                        />
                      ))}
                    </ul>
                  </section>
                ))
              ) : q.trim() ? (
                <EmptyState
                  title={`No calls mention "${q.trim()}".`}
                  action={
                    <Button variant="outline" size="sm" onClick={() => setQ("")}>
                      Clear search
                    </Button>
                  }
                />
              ) : (
                <EmptyState title={emptyTitle(tab, line, list.hasNextPage)} />
              )}
              {/* Two thousand calls, sixty at a time: without this the rest of
                  the year was in the database and nowhere else. */}
              {list.hasNextPage ? (
                <div className="flex flex-col items-center gap-1.5 px-3 pt-1 pb-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    disabled={list.isFetchingNextPage}
                    onClick={() => void list.fetchNextPage()}
                  >
                    {list.isFetchingNextPage ? "Loading…" : "Load older calls"}
                  </Button>
                  <span className="text-faint text-xs tabular-nums">
                    {fetched.length} of {total} loaded
                  </span>
                </div>
              ) : fetched.length > PAGE ? (
                <p className="text-faint px-3 pt-1 pb-3 text-center text-xs tabular-nums">
                  All {total} calls loaded
                </p>
              ) : null}
            </>
          )}
        </div>
      </aside>

      <section className={cn("min-w-0 flex-1 md:overflow-y-auto", c ? "block" : "hidden md:block")}>
        {shown ? (
          <>
            <Link
              to="/ws/sarah/calls"
              search={listSearch(tab, line, needle)}
              className="text-muted-foreground hover:text-foreground flex min-h-11 w-fit items-center gap-1 px-4 pt-1 text-sm md:hidden"
            >
              <ArrowLeft className="size-4" /> Calls
            </Link>
            <CallPane key={shown} callId={shown} />
          </>
        ) : (
          <div className="text-muted-foreground flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
            <span className="flex size-12 items-center justify-center rounded-full bg-sarah/10 text-sarah">
              <Phone className="size-5" />
            </span>
            <p className="text-sm">Pick a call to read what was said and what happens next.</p>
          </div>
        )}
      </section>
    </div>
  );
}
